import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { pnlModeBunja } from '@flipster/features/src/shared/pnl-setting-modal/state';
import { getDisplayPnl, getDisplayPnlPercent } from '@flipster/features/src/shared/pnl/get-display-pnl';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { OpenPosition } from '@flipster/r230522/original/position';
import { pswapTickersBunja } from '@flipster/r230522/original/pswap-ticker';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { getChartLinkedTickerPrice } from '@flipster/tvchart/src/misc/chart-price-type';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { pipChartPriceType, pipUpnlBasis } from './constants';

interface Props {
  position: OpenPosition;
}

const PositionItem = ({ position }: Props) => {
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const instrument = instrumentsKeyBySymbol?.[position.symbol];
  const { pnlModeAtom } = useBunja(pnlModeBunja);
  const { pswapTickersAtom } = useBunja(pswapTickersBunja);
  const pnlMode = useAtomValue(pnlModeAtom);
  const tickers = useAtomValue(pswapTickersAtom);
  const lastPrice = getChartLinkedTickerPrice(tickers?.[position.symbol], pipChartPriceType);

  const displayOptions = useMemo(() => ({ pnlMode, upnlBasis: pipUpnlBasis }), [pnlMode]);
  const displayPnl = useMemo(() => getDisplayPnl(position, displayOptions), [position, displayOptions]);
  const displayPnlPercent = useMemo(() => getDisplayPnlPercent(position, displayOptions), [position, displayOptions]);

  return (
    <div
      className={flex({ p: 12, gap: 8, flexDir: 'column', background: 'background.default.level-3', borderRadius: 8 })}
    >
      <div className={stack({ gap: 4 })}>
        <div>
          <SymbolDisplay
            symbol={position.symbol}
            icon={<SymbolDisplay.Icon size={20} space={8} />}
            mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/S/Strong' }} />}
            subLabel={
              <SymbolDisplay.Label area='sub' css={{ color: 'content.default.level-2', textStyle: 'Text/S/Regular' }}>
                <Formatted.Number
                  method='round-half-up'
                  value={lastPrice}
                  precision={instrument?.pricePrecision}
                  toFixed
                  withComma
                />
              </SymbolDisplay.Label>
            }
          />
        </div>
        <div className={css({ display: 'flex', gap: 4 })}>
          <OrderSideBadge orderSide={position.side} />
          <LeverageBadge leverage={position.initLeverage} marginType={position.marginType} />
        </div>
      </div>
      <div className={stack({ gap: 2, textStyle: 'Text/M/Strong' })}>
        <Formatted.Number value={displayPnl} precision={UsdtPrecision} toFixed withComma withPlus withColor />
        <Formatted.Percent
          value={displayPnlPercent}
          toFixed
          withComma
          withPlus
          withColor={Num(displayPnl).abs().gte('0.01')}
          withParentheses
        />
      </div>
    </div>
  );
};

export default PositionItem;
